import React from 'react';
import { CareProgramTopicCard } from 'components/CareProgramTopicCard/CareProgramTopicCard';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useRecommendedCarePrograms } from 'features/resources/hooks';
import { PendoEventType } from 'features/pendo/types';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { ResourceCardGrid, ResourceCardGridCell } from './ResourceCardGrid';
import { ResourceSection } from './ResourceSection';

/**
 * NOTE: This component currently calls useRecommendedCarePrograms hook directly because it's
 * only used for desktop viewport. This makes the child component complex to control for different view ports
 * with its own data fetching.
 * FUTURE CONSIDERATION: If this component needs to be displayed across all viewports (mobile/tablet/desktop),
 * consider moving the useRecommendedCarePrograms hook to the parent (useResources/Resources component)
 * and pass the data as props. This avoid duplicate API calls when both parent and child need the same data.
 */
export const RecommendedCareProgram = () => {
  const { translation } = useTranslationContext();
  const navigate = useNavigate();
  const { trackEvent } = usePendo();
  const { recommendedPrograms, isLoading } = useRecommendedCarePrograms();

  if (isLoading || recommendedPrograms?.length === 0) {
    return null;
  }

  const handleViewAll = () => {
    trackEvent(PendoEventType.ALL_CARE_PROGRAMS, {
      eventType: PendoEventType.ALL_CARE_PROGRAMS,
    });
    navigate('/resources/topics');
  };

  const handleProgramClick = (programId: number) => {
    navigate(`/resources/topics?programId=${programId}`);
  };

  return (
    <ResourceSection
      description={translation('featured_care_programs')}
      title={translation('care_program_card_title')}
      titleId="recommended_programs"
      viewAllText={translation('care_programs_cta')}
      viewAllAriaLabel={`${translation('care_programs_cta')} - ${translation('care_program_card_title')}`}
      onViewAll={handleViewAll}
    >
      <ResourceCardGrid>
        {recommendedPrograms.map((program) => (
          <ResourceCardGridCell key={program.id}>
            <CareProgramTopicCard
              healthTopic={program.healthTopic}
              title={program.title}
              ariaLabel={`${program.title} care program`}
              onClick={() => handleProgramClick(program.id)}
            />
          </ResourceCardGridCell>
        ))}
      </ResourceCardGrid>
    </ResourceSection>
  );
};
